import { useState, useEffect, useRef, useCallback } from 'react';

interface SleepTimerOptions {
  onTimerEnd: () => void;
  onTimerUpdate?: (remainingTime: number) => void;
}

export function useSleepTimer({ onTimerEnd, onTimerUpdate }: SleepTimerOptions) {
  const [isActive, setIsActive] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0); // in seconds
  const [initialTime, setInitialTime] = useState(0);
  
  const timerRef = useRef<number>();
  const endTimeRef = useRef<number>();

  const presetTimes = [
    { label: '15 minutes', value: 15 * 60 },
    { label: '30 minutes', value: 30 * 60 },
    { label: '45 minutes', value: 45 * 60 },
    { label: '1 hour', value: 60 * 60 },
    { label: '1.5 hours', value: 90 * 60 },
    { label: '2 hours', value: 120 * 60 },
  ];

  const startTimer = useCallback((minutes: number) => {
    const seconds = minutes * 60;
    const endTime = Date.now() + (seconds * 1000);
    
    setInitialTime(seconds);
    setRemainingTime(seconds);
    setIsActive(true);
    endTimeRef.current = endTime;

    // Save timer state to localStorage for persistence
    localStorage.setItem('playfm_sleep_timer', JSON.stringify({
      endTime,
      initialTime: seconds,
      isActive: true,
    }));

    // Start countdown
    timerRef.current = window.setInterval(() => {
      const now = Date.now();
      const remaining = Math.max(0, Math.ceil((endTimeRef.current! - now) / 1000));
      
      setRemainingTime(remaining);
      onTimerUpdate?.(remaining);

      if (remaining <= 0) {
        stopTimer();
        onTimerEnd();
      }
    }, 1000);
  }, [onTimerEnd, onTimerUpdate]);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = undefined;
    }
    
    setIsActive(false);
    setRemainingTime(0);
    setInitialTime(0);
    endTimeRef.current = undefined;
    
    // Clear from localStorage
    localStorage.removeItem('playfm_sleep_timer');
  }, []);

  const addTime = useCallback((minutes: number) => {
    if (!isActive || !endTimeRef.current) return;
    
    const additionalSeconds = minutes * 60;
    endTimeRef.current += additionalSeconds * 1000;
    setInitialTime(prev => prev + additionalSeconds);
    
    // Update localStorage
    localStorage.setItem('playfm_sleep_timer', JSON.stringify({
      endTime: endTimeRef.current,
      initialTime: initialTime + additionalSeconds,
      isActive: true,
    }));
  }, [isActive, initialTime]);

  const formatTime = useCallback((seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    } else {
      return `${minutes}:${secs.toString().padStart(2, '0')}`;
    }
  }, []);

  const getProgress = useCallback((): number => {
    if (initialTime === 0) return 0;
    return ((initialTime - remainingTime) / initialTime) * 100;
  }, [initialTime, remainingTime]);

  // Restore timer on component mount
  useEffect(() => {
    try {
      const savedTimer = localStorage.getItem('playfm_sleep_timer');
      if (savedTimer) {
        const { endTime, initialTime: savedInitialTime, isActive: savedIsActive } = JSON.parse(savedTimer);
        
        if (savedIsActive && endTime > Date.now()) {
          const remaining = Math.ceil((endTime - Date.now()) / 1000);
          setInitialTime(savedInitialTime);
          setRemainingTime(remaining);
          setIsActive(true);
          endTimeRef.current = endTime;

          // Resume countdown
          timerRef.current = window.setInterval(() => {
            const now = Date.now();
            const remaining = Math.max(0, Math.ceil((endTimeRef.current! - now) / 1000));
            
            setRemainingTime(remaining);
            onTimerUpdate?.(remaining);

            if (remaining <= 0) {
              stopTimer();
              onTimerEnd();
            }
          }, 1000);
        } else {
          // Timer expired while app was closed
          localStorage.removeItem('playfm_sleep_timer');
        }
      }
    } catch (error) {
      console.error('Error restoring sleep timer:', error);
      localStorage.removeItem('playfm_sleep_timer');
    }
  }, [onTimerEnd, onTimerUpdate, stopTimer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  return {
    isActive,
    remainingTime,
    initialTime,
    presetTimes,
    startTimer,
    stopTimer,
    addTime,
    formatTime,
    getProgress,
  };
}